import * as Sentry from "@sentry/react";
import type { AxiosError, InternalAxiosRequestConfig } from "axios";
import { type ErrorInfo, useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import { getApiRequestMeta, getCurrentRoutePath } from "@/lib/analytics/api-utils";
import {
  normalizeErrorName,
  sanitizeErrorMessage,
  sanitizeErrorStack,
  sanitizePathname,
} from "@/lib/analytics/error-utils";
import { FE_REQUEST_ID_PROPERTY } from "@/lib/request-id";

type SafeContextValue = string | number | boolean | null | undefined;
type SanitizedPayload = SafeContextValue | SanitizedPayload[] | { [key: string]: SanitizedPayload };

export interface SentryUserContext {
  userId?: SafeContextValue;
  username?: SafeContextValue;
  email?: SafeContextValue;
  staffId?: SafeContextValue;
  role?: SafeContextValue;
  specialtyId?: SafeContextValue;
  permissionsCount?: SafeContextValue;
  organizationId?: SafeContextValue;
  clinicId?: SafeContextValue;
}

const SENSITIVE_KEY_PATTERN =
  /token|authorization|password|secret|email|phone|mobile|tel|name|address|patient|ic|nric|passport|birth|dob|idnumber/i;
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const PHONE_PATTERN = /\+?\d[\d\s-]{6,}\d/g;
const LONG_NUMBER_PATTERN = /\b\d{6,}\b/g;

const sentryEnvironment = import.meta.env.VITE_ENV || import.meta.env.MODE || "development";
const isDevelopmentEnvironment =
  sentryEnvironment === "development" ||
  import.meta.env.DEV ||
  import.meta.env.MODE === "development";
const sentryEnabled =
  !isDevelopmentEnvironment &&
  import.meta.env.VITE_SENTRY_ENABLED !== "false" &&
  Boolean(import.meta.env.VITE_SENTRY_DSN);
const sentryRelease =
  import.meta.env.VITE_SENTRY_RELEASE ||
  import.meta.env.VITE_RELEASE ||
  import.meta.env.VITE_APP_VERSION;
const allowSentryUserPii = sentryEnvironment === "staging" || sentryEnvironment === "production";

// 401 is an expected auth-recovery signal; the app handles it with refresh/re-login locally.
const IGNORED_SENTRY_HTTP_STATUS = 401;

export const isSentryEnabled = () => sentryEnabled;

const parseSampleRate = (value: unknown): number | undefined => {
  if (value == null || value === "") return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return undefined;
  return Math.min(Math.max(parsed, 0), 1);
};

const getDefaultTracesSampleRate = () => (sentryEnvironment === "production" ? 0.1 : 1);

const isIgnoredHttpStatus = (value: unknown) => Number(value) === IGNORED_SENTRY_HTTP_STATUS;

const readHttpStatus = (value: unknown) => {
  if (!value || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  return record.api_status ?? record.status ?? record.statusCode ?? record.status_code;
};

const readHttpStatusFromError = (error: unknown) => {
  if (!error || typeof error !== "object") return undefined;
  const record = error as Record<string, unknown>;
  return readHttpStatus(record) ?? readHttpStatus(record.response);
};

const shouldSkipSentryContext = (context?: Record<string, unknown>) => {
  if (isDevelopmentEnvironment) return true;
  return isIgnoredHttpStatus(readHttpStatus(context));
};

const shouldSkipSentryCapture = (error: unknown, context?: Record<string, unknown>) => {
  if (shouldSkipSentryContext(context)) return true;
  return isIgnoredHttpStatus(readHttpStatusFromError(error));
};

const shouldSkipSentryEvent = (event: Sentry.Event) => {
  if (isDevelopmentEnvironment) return true;

  if (isIgnoredHttpStatus(readHttpStatus(event.tags))) return true;
  if (isIgnoredHttpStatus(readHttpStatus(event.extra))) return true;

  const contexts = event.contexts as Record<string, unknown> | undefined;
  if (isIgnoredHttpStatus(readHttpStatus(contexts?.api))) return true;
  if (isIgnoredHttpStatus(readHttpStatus(contexts?.safe_context))) return true;

  return false;
};

const sanitizeString = (value: string, maxLen = 300) =>
  value
    .replace(EMAIL_PATTERN, "[redacted-email]")
    .replace(PHONE_PATTERN, "[redacted-phone]")
    .replace(LONG_NUMBER_PATTERN, "[redacted-number]")
    .slice(0, maxLen);

const sanitizeUrl = (value: string, maxLen = 500) => {
  try {
    const parsed = new URL(value, window.location.origin);
    return sanitizeString(`${parsed.origin}${sanitizePathname(parsed.pathname)}`, maxLen);
  } catch {
    return sanitizeString(value.split("?")[0] || value, maxLen);
  }
};

const sanitizePrimitive = (value: unknown): SafeContextValue => {
  if (typeof value === "string") return sanitizeString(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (value == null) return value;
  return undefined;
};

const sanitizePayload = (value: unknown, depth = 0): SanitizedPayload => {
  if (depth > 3) return "[redacted-depth-limit]";

  const primitive = sanitizePrimitive(value);
  if (primitive !== undefined) return primitive;

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizePayload(item, depth + 1))
      .filter((item) => item !== undefined);
  }

  if (!value || typeof value !== "object") return undefined;

  const sanitized: { [key: string]: SanitizedPayload } = {};
  for (const [key, nestedValue] of Object.entries(value)) {
    if (SENSITIVE_KEY_PATTERN.test(key)) continue;
    const safeValue = sanitizePayload(nestedValue, depth + 1);
    if (safeValue !== undefined) {
      sanitized[key] = safeValue;
    }
  }

  return Object.keys(sanitized).length ? sanitized : undefined;
};

const sanitizeContext = (context?: Record<string, unknown>) => {
  if (!context) return undefined;

  const sanitized: Record<string, SafeContextValue> = {};
  for (const [key, value] of Object.entries(context)) {
    if (SENSITIVE_KEY_PATTERN.test(key)) continue;
    const safeValue = sanitizePrimitive(value);
    if (safeValue !== undefined) {
      sanitized[key] = safeValue;
    }
  }

  return Object.keys(sanitized).length ? sanitized : undefined;
};

const getSafeUserId = (context: SentryUserContext) => {
  const userId = sanitizePrimitive(context.userId);
  if (typeof userId === "string" && userId) return userId;
  const staffId = sanitizePrimitive(context.staffId);
  if (staffId != null) return String(staffId);
  return undefined;
};

const getApprovedUserPii = (value: SafeContextValue) => {
  if (!allowSentryUserPii || typeof value !== "string") return undefined;

  const normalized = value.trim();
  return normalized || undefined;
};

const sanitizeEventUser = (user: unknown) => {
  if (!user || typeof user !== "object") return undefined;

  const rawId = "id" in user ? (user as { id?: unknown }).id : undefined;
  const id = sanitizePrimitive(rawId);
  if (id == null || id === "") return undefined;

  return { id: String(id) };
};

export const initSentry = () => {
  if (!sentryEnabled) {
    if (isDevelopmentEnvironment && import.meta.env.VITE_SENTRY_DSN) {
      console.warn("[Sentry] Disabled in development environment.");
    } else if (import.meta.env.DEV) {
      console.warn("[Sentry] Disabled. Configure VITE_SENTRY_DSN to enable reporting.");
    }
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: sentryEnvironment,
    release: sentryRelease,
    sendDefaultPii: false,
    tracesSampleRate:
      parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) ??
      getDefaultTracesSampleRate(),
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        createRoutesFromChildren,
        matchRoutes,
        useEffect,
        useLocation,
        useNavigationType,
      }),
    ],
    beforeSend(event) {
      if (shouldSkipSentryEvent(event)) {
        return null;
      }

      if (event.request) {
        delete event.request.cookies;
        delete event.request.headers;
        delete event.request.data;
        if (event.request.url) {
          event.request.url = sanitizeUrl(event.request.url, 500);
        }
      }

      if (event.message) {
        event.message = sanitizeString(event.message, 500);
      }

      event.user = sanitizeEventUser(event.user);

      if (event.contexts) {
        event.contexts = sanitizePayload(event.contexts) as typeof event.contexts;
      }

      if (event.extra) {
        event.extra = sanitizePayload(event.extra) as typeof event.extra;
      }

      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        breadcrumb.data = sanitizeContext(breadcrumb.data);
      }

      if (breadcrumb.message) {
        breadcrumb.message = sanitizeUrl(breadcrumb.message, 300);
      }

      return breadcrumb;
    },
  });
};

export const captureReactError = (error: Error, errorInfo: ErrorInfo) => {
  if (!sentryEnabled || shouldSkipSentryCapture(error)) return;
  Sentry.captureReactException(error, errorInfo, {
    mechanism: { handled: true, type: "react.error_boundary" },
  });
};

export const captureGlobalError = (
  error: unknown,
  label: string,
  context?: Record<string, unknown>,
) => {
  if (!sentryEnabled || shouldSkipSentryCapture(error, context)) return;

  Sentry.withScope((scope) => {
    scope.setTag("error_source", label);
    scope.setTag("route_path", getCurrentRoutePath());
    const requestId = sanitizePrimitive(context?.[FE_REQUEST_ID_PROPERTY]);
    if (requestId) {
      scope.setTag(FE_REQUEST_ID_PROPERTY, String(requestId));
    }
    const sanitized = sanitizeContext(context);
    if (sanitized) {
      scope.setContext("safe_context", sanitized);
    }

    if (error instanceof Error) {
      Sentry.captureException(error);
      return;
    }

    Sentry.captureException(new Error(sanitizeErrorMessage(error || label)));
  });
};

export const captureAxiosError = (error: AxiosError, context?: Record<string, unknown>) => {
  if (
    !sentryEnabled ||
    shouldSkipSentryContext(context) ||
    isIgnoredHttpStatus(error.response?.status)
  ) {
    return;
  }

  const requestMeta = error.config
    ? getApiRequestMeta(error.config as InternalAxiosRequestConfig)
    : undefined;

  Sentry.withScope((scope) => {
    scope.setTag("error_source", "api");
    scope.setTag("api_method", requestMeta?.method || "UNKNOWN");
    scope.setTag("api_path", requestMeta?.path || "/unknown");
    scope.setTag("route_path", getCurrentRoutePath());
    const requestId = sanitizePrimitive(context?.[FE_REQUEST_ID_PROPERTY]);
    if (requestId) {
      scope.setTag(FE_REQUEST_ID_PROPERTY, String(requestId));
    }

    scope.setContext("api", {
      method: requestMeta?.method,
      path: requestMeta?.path,
      host: requestMeta?.host,
      status: error.response?.status,
      [FE_REQUEST_ID_PROPERTY]: requestId,
    });

    const sanitized = sanitizeContext(context);
    if (sanitized) {
      scope.setContext("safe_context", sanitized);
    }

    Sentry.captureException(error);
  });
};

export const addSentryBreadcrumb = (
  category: string,
  message: string,
  data?: Record<string, unknown>,
) => {
  if (!sentryEnabled || shouldSkipSentryContext(data)) return;

  Sentry.addBreadcrumb({
    category,
    message: sanitizeUrl(message, 200),
    data: sanitizeContext(data),
    level: "info",
  });
};

export const setSentryUserContext = (context: SentryUserContext) => {
  if (!sentryEnabled) return;

  const userId = getSafeUserId(context);
  const username = getApprovedUserPii(context.username);
  const email = getApprovedUserPii(context.email);
  Sentry.setUser(
    userId
      ? {
          id: userId,
          ...(username ? { username } : {}),
          ...(email ? { email } : {}),
        }
      : null,
  );
  Sentry.setContext("user_context", {
    staff_id: sanitizePrimitive(context.staffId),
    role: sanitizePrimitive(context.role),
    specialty_id: sanitizePrimitive(context.specialtyId),
    permissions_count: sanitizePrimitive(context.permissionsCount),
  });
  Sentry.setContext("healthcare_context", {
    organization_id: sanitizePrimitive(context.organizationId),
    clinic_id: sanitizePrimitive(context.clinicId),
  });
  Sentry.setTag("organization_id", String(sanitizePrimitive(context.organizationId) || ""));
  Sentry.setTag("clinic_id", String(sanitizePrimitive(context.clinicId) || ""));
};

export const clearSentryUserContext = () => {
  if (!sentryEnabled) return;
  Sentry.setUser(null);
  Sentry.setContext("user_context", null);
  Sentry.setContext("healthcare_context", null);
  Sentry.setTag("organization_id", undefined);
  Sentry.setTag("clinic_id", undefined);
};

export const getSentryReactRootOptions = () => {
  if (!sentryEnabled) return undefined;

  return {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught React error:", error, errorInfo.componentStack);
    }),
    onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Recoverable React error:", error, errorInfo.componentStack);
    }),
  };
};

export const getSentryErrorMetadata = (error: unknown, componentStack?: string) => ({
  error_name: normalizeErrorName(error),
  error_stack: sanitizeErrorStack(error instanceof Error ? error.stack : undefined),
  component_stack: sanitizeErrorStack(componentStack),
  route_path: sanitizePathname(window.location.pathname),
});
